/**
 * "Copy CCO" — the Call Coming Out message for the crew WhatsApp group.
 *
 * The text is rendered by the API, not here. That matters for two reasons: the
 * template is tenant-editable so the browser must not hold a second copy of the
 * layout, and generating it writes a disclosure record. A client-side render
 * would produce text nobody could later prove was sent.
 *
 * The message is shown before it is copied. A dispatcher about to paste patient
 * detail into a group of forty people should see exactly what they are sending.
 */

import { useState } from 'react';
import { api, ApiError, type RenderedMessage } from '../api/client.js';

/** Placeholder names the crew would actually notice missing, in plain words. */
const MATTERS: Record<string, string> = {
  'scene.address': 'location',
  'triage.chiefComplaint': 'chief complaint',
  'vehicles.callSigns': 'vehicle',
  'contact.onScenePhone': 'on-scene number',
  'client.name': 'client',
};

export function CcoButton({ caseId }: { caseId: string }) {
  const [message, setMessage] = useState<RenderedMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      setMessage(await api.get<RenderedMessage>(`/cases/${caseId}/messages/cco`));
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 404
          ? 'No CCO template is configured for this tenant.'
          : e instanceof Error
            ? e.message
            : 'Could not build the message',
      );
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!message) return;
    try {
      await navigator.clipboard.writeText(message.text);
      setCopied(true);
    } catch {
      // Clipboard access is refused in some embedded browsers and over plain
      // HTTP. Say so rather than appearing to succeed — the text is on screen
      // and can be selected by hand.
      setError('Could not reach the clipboard. Select the text above and copy it.');
    }
  };

  const missing = message?.missing.filter((m) => m in MATTERS).map((m) => MATTERS[m]) ?? [];

  return (
    <div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void load()}
          disabled={busy}
          className="rounded bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-500 disabled:opacity-50"
        >
          {busy ? 'Building…' : message ? 'Refresh CCO' : 'Copy CCO'}
        </button>
        {message && (
          <button
            type="button"
            onClick={() => void copy()}
            className="rounded bg-slate-700 px-3 py-1.5 text-sm font-medium text-slate-100 hover:bg-slate-600"
          >
            {copied ? 'Copied ✓' : 'Copy to clipboard'}
          </button>
        )}
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-400" role="alert">
          {error}
        </p>
      )}

      {message && (
        <>
          {missing.length > 0 && (
            <p className="mt-2 text-xs text-amber-400">
              Not filled in yet: {missing.join(', ')}. It will read N/A.
            </p>
          )}
          <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap rounded border border-slate-700 bg-slate-950 p-3 font-sans text-sm text-slate-200">
            {message.text}
          </pre>
          <p className="mt-2 text-xs text-slate-500">
            Contains patient detail. Sharing it is recorded against your name.
          </p>
        </>
      )}
    </div>
  );
}
